import React, { useState } from "react";
import {
  LayoutDashboard,
  Package,
  Users,
  ShoppingCart,
  TrendingUp,
  CreditCard,
  UserCheck,
  Settings,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useAuth } from "../context/AuthContext";
import { Role } from "../types/models";
import { getInitials, humanizeEnum } from "../utils/helpers";
import { NotificationsMenu } from "./NotificationsMenu";
import { toast } from "./ui/Toast";

interface NavItem {
  path: string;
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  /** Roles allowed to see this item; omitted = everyone authenticated. */
  roles?: Role[];
}

const MANAGEMENT: Role[] = [Role.ADMIN, Role.MANAGER];
const STAFF: Role[] = [
  Role.ADMIN,
  Role.MANAGER,
  Role.STORE_KEEPER,
  Role.PRODUCTION_SUPERVISOR,
];

const NAV_ITEMS: NavItem[] = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: MANAGEMENT },
  { path: "/inventory", icon: Package, label: "Inventory", roles: STAFF },
  { path: "/production", icon: TrendingUp, label: "Production", roles: STAFF },
  { path: "/orders", icon: ShoppingCart, label: "Orders" },
  { path: "/clients", icon: Users, label: "Clients", roles: MANAGEMENT },
  { path: "/payments", icon: CreditCard, label: "Payments" },
  { path: "/staff", icon: UserCheck, label: "Staff", roles: MANAGEMENT },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const menuItems = NAV_ITEMS.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role)),
  );
  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen bg-slate-950 text-slate-100">
      {/* Sidebar */}
      <aside
        className={clsx(
          "fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-slate-700 bg-slate-900 transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20",
        )}
      >
        <div className="flex h-20 items-center justify-between border-b border-slate-700 p-4">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 font-bold">
                F
              </div>
              <span className="text-lg font-bold">Factory MIS</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            className="rounded-lg p-2 transition-colors hover:bg-slate-800"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto p-4">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={clsx(
                "flex items-center gap-3 rounded-lg px-4 py-3 transition-all",
                isActive(item.path)
                  ? "bg-blue-600 text-white shadow-lg"
                  : "text-slate-400 hover:bg-slate-800 hover:text-slate-100",
              )}
              title={!sidebarOpen ? item.label : undefined}
            >
              <item.icon size={20} />
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="border-t border-slate-700 p-4">
          <button
            onClick={handleLogout}
            className={clsx(
              "flex w-full items-center gap-3 rounded-lg px-4 py-3 text-red-400 transition-colors hover:bg-red-900/20",
              !sidebarOpen && "justify-center",
            )}
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div
        className={clsx(
          "flex flex-1 flex-col transition-all duration-300",
          sidebarOpen ? "ml-64" : "ml-20",
        )}
      >
        <header className="flex h-20 items-center justify-end gap-6 border-b border-slate-700 bg-slate-900 px-8">
          <NotificationsMenu />
          <div className="flex items-center gap-3 border-l border-slate-700 pl-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-blue-500 text-sm font-bold">
              {user ? getInitials(user.fullName) : "?"}
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-semibold">{user?.fullName}</div>
              <div className="text-xs capitalize text-slate-400">
                {humanizeEnum(user?.role)}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">
          <div className="animate-slideIn">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
